/**
 * Claude-Desktop-Zugang (Jörg 2026-10-28): ein Schalter für die Test-Personas
 * claude-*@test.*, mit denen Claude Desktop Testszenarien im Browser durchspielt.
 *
 * Sperren sperrt die Anmeldung (locked) und beendet alle Sitzungen (Refresh-Tokens) — die Konten
 * bleiben aktive Mitarbeiter, also in Zeitkonten/Plantafel sichtbar;
 * eine gerade laufende Anmeldung hält höchstens noch ihre Token-Laufzeit (15 min).
 * Einzel-Aktion mit Rückfrage beim Sperren und sichtbarer Rückmeldung.
 */
import { useCallback, useEffect, useState } from 'react';
import { Bot, Loader2, Lock, Unlock, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { ConfirmModal } from '@onvice/portal-ui';
import { testCockpitApi, type ClaudeDesktopState } from '../../../lib/testCockpitApi';

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ClaudeDesktopAccessPanel() {
  const [state, setState] = useState<ClaudeDesktopState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [confirmLock, setConfirmLock] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setState(await testCockpitApi.claudeDesktopState());
    } catch (e) {
      setError((e as Error).message || 'Zugänge konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function apply(active: boolean) {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const r = await testCockpitApi.claudeDesktopSet(active);
      setState({ active: r.active, accounts: r.accounts });
      setInfo(active
        ? `Zugang aktiviert (${r.changed} Konten).`
        : `Zugang gesperrt (${r.changed} Konten, ${r.sessionsEnded} Sitzungen beendet).`);
    } catch (e) {
      setError((e as Error).message || 'Umschalten fehlgeschlagen');
    } finally {
      setBusy(false);
      setConfirmLock(false);
    }
  }

  const accounts = state?.accounts ?? [];
  const isActive = state?.active ?? false;

  return (
    <section className="rounded-lg border border-default p-4 space-y-4" data-test-anchor="claude-desktop">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Bot className="w-5 h-5 mt-0.5 text-brand-500" />
          <div>
            <h2 className="text-lg font-semibold">Claude-Desktop-Zugang</h2>
            <p className="text-sm text-muted-foreground">
              Test-Konten, mit denen Claude Desktop Testszenarien im Browser durchspielt (Handwerker, Büro, Geschäftsführung).
              Gesperrt kann sich Claude nicht anmelden; laufende Sitzungen werden beendet.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading || busy}
          className="p-2 rounded hover:bg-muted text-muted-foreground disabled:opacity-50"
          title="Neu laden"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded bg-rose-500/10 text-rose-700 dark:text-rose-300 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}
      {info && (
        <div className="flex items-start gap-2 p-3 rounded bg-brand-500/10 text-brand-700 dark:text-brand-300 text-sm">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /><span>{info}</span>
        </div>
      )}

      {loading && !state ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>
      ) : accounts.length === 0 ? (
        <div className="text-sm text-muted-foreground">Keine Claude-Konten (claude-…@test.…) auf dieser Instanz angelegt.</div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-sm px-2 py-1 rounded font-medium ${isActive
              ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300'
              : 'bg-rose-500/15 text-rose-700 dark:text-rose-300'}`}
            >
              {isActive ? 'Aktiv' : 'Gesperrt'}
            </span>
            {isActive ? (
              <button
                type="button"
                onClick={() => setConfirmLock(true)}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-2 rounded bg-rose-600 hover:bg-rose-500 text-white text-sm disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Zugang sperren
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void apply(true)}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} Zugang aktivieren
              </button>
            )}
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-default">
                <th className="py-2 pr-3 font-medium">Konto</th>
                <th className="py-2 pr-3 font-medium">Name</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Letzte Anmeldung</th>
                <th className="py-2 font-medium">Sitzungen</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id} className="border-b border-default last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{a.email}</td>
                  <td className="py-2 pr-3">{[a.first_name, a.last_name].filter(Boolean).join(' ')}</td>
                  <td className="py-2 pr-3">
                    {a.locked || a.auth_active === false ? 'gesperrt' : 'aktiv'}
                  </td>
                  <td className="py-2 pr-3">{fmtDateTime(a.last_login_at)}</td>
                  <td className="py-2">{a.sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <ConfirmModal
        open={confirmLock}
        title="Claude-Desktop-Zugang sperren?"
        message="Alle Claude-Konten werden gesperrt und ihre Sitzungen beendet. Ein Test, der gerade läuft, bricht ab."
        confirmLabel="Sperren"
        danger
        busy={busy}
        onClose={() => setConfirmLock(false)}
        onConfirm={() => void apply(false)}
      />
    </section>
  );
}
